// client/src/components/marketer/result/modals/ResultViewModal.js
import React, { useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import '../../../../styles/marketer/modals/ResultViewModal.css';

export default function ResultViewModal({ open, onClose, result }) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  if (!open || !result) return null;

  const images = result.resultImages || [];
  const urls = result.resultUrls || [];
  const note = result.resultNote || '';
  const projectNumber = result.projectNumber || 'PROJECT';
  const submittedAt = result.resultSubmittedAt 
    ? new Date(result.resultSubmittedAt).toLocaleString('ko-KR')
    : 'N/A';
  
  // ✅ 인플루언서 정보 (관리자 화면에서 전달됨)
  const influencerInfo = result.influencerInfo || null;

  // ✅ 단일 이미지 다운로드
  const handleDownloadImage = async (imageUrl) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const ext = blob.type.split('/')[1] || 'jpg';
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${projectNumber}_image_${Date.now()}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error('이미지 다운로드 실패:', error);
      alert('이미지 다운로드에 실패했습니다.');
    }
  };

  // ✅ 전체 다운로드 (ZIP 파일로)
  const handleDownloadAll = async () => {
    if (images.length === 0) {
      alert('다운로드할 이미지가 없습니다.');
      return;
    }

    setIsDownloading(true);
    
    try {
      const zip = new JSZip();
      const folder = zip.folder('images');
      
      // 모든 이미지를 fetch하여 ZIP에 추가
      const promises = images.map(async (imageUrl, index) => {
        try {
          const response = await fetch(imageUrl);
          if (!response.ok) throw new Error('이미지 로드 실패');
          
          const blob = await response.blob();
          const ext = blob.type.split('/')[1] || 'jpg';
          const filename = `image_${String(index + 1).padStart(2, '0')}.${ext}`;
          
          folder.file(filename, blob);
        } catch (error) {
          console.error(`이미지 ${index + 1} 다운로드 실패:`, error);
        }
      });

      await Promise.all(promises);

      // URL 목록이 있으면 텍스트 파일로 추가
      if (urls.length > 0) {
        const urlText = urls.map((url, idx) => `${idx + 1}. ${url}`).join('\n');
        zip.file('urls.txt', urlText);
      }

      // 메모가 있으면 텍스트 파일로 추가
      if (note) {
        zip.file('note.txt', note);
      }

      // ZIP 파일 생성 및 다운로드
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${projectNumber}_결과물.zip`);

    } catch (error) {
      console.error('ZIP 다운로드 실패:', error);
      alert('ZIP 파일 생성에 실패했습니다.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePrevImage = () => {
    setCurrentImageIndex(prev => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex(prev => (prev < images.length - 1 ? prev + 1 : 0));
  };

  // ✅ URL 정규화 함수
  const normalizeUrl = (url) => {
    if (!url) return '#';
    
    const trimmedUrl = url.trim();
    
    // 이미 프로토콜이 있으면 그대로 반환
    if (/^https?:\/\//i.test(trimmedUrl)) {
      return trimmedUrl;
    }
    
    // 프로토콜이 없으면 https:// 추가
    return `https://${trimmedUrl}`;
  };

  return (
    <div className="result-view-modal mbm-overlay">
      <div className="result-view-modal__content mbm" onClick={(e) => e.stopPropagation()}>
        {/* 닫기 버튼 */}
        <button onClick={onClose} className="result-view-modal__close mbm-close" aria-label="닫기">×</button>

        {/* 헤더 */}
        <h2 className="result-view-modal__title mbm-title">📋 제출된 결과물</h2>
        <div className="result-view-modal__subtitle">
          <span>👤 인플루언서: <strong>{result.influencerName}</strong></span>
          <span style={{ color: '#cbd5e1' }}>|</span>
          <span>📅 제출일: <strong>{submittedAt}</strong></span>
        </div>
        
        {/* ✅ 인플루언서 상세 정보 (관리자 화면에서만 표시) */}
        {influencerInfo && (
          <div style={{
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            border: '1px solid #bae6fd',
            borderRadius: 12,
            padding: 16,
            marginBottom: 24,
            fontSize: 13
          }}>
            <div style={{ fontWeight: 700, marginBottom: 10, color: '#0369a1', fontSize: 14 }}>
              📋 인플루언서 상세 정보
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, color: '#334155' }}>
              <div>
                <span style={{ color: '#64748b', fontSize: 12 }}>이름: </span>
                <strong>{influencerInfo.userName}</strong>
              </div>
              <div>
                <span style={{ color: '#64748b', fontSize: 12 }}>아이디: </span>
                <strong>{influencerInfo.userId}</strong>
              </div>
              <div>
                <span style={{ color: '#64748b', fontSize: 12 }}>이메일: </span>
                <span>{influencerInfo.email || '-'}</span>
              </div>
              <div>
                <span style={{ color: '#64748b', fontSize: 12 }}>연락처: </span>
                <span>{influencerInfo.phoneNumber || '-'}</span>
              </div>
            </div>
          </div>
        )}

        {/* 컨텐츠 */}
        <div className="result-view-modal__content-wrapper">
          {/* 이미지 섹션 */}
          {images.length > 0 && (
            <div className="result-view-modal__section">
              <div className="result-view-modal__section-header">
                <h3 className="result-view-modal__section-title">
                  🖼️ 첨부 이미지
                  <span style={{ 
                    marginLeft: 8, 
                    fontSize: 13, 
                    fontWeight: 600, 
                    color: '#2f6bff',
                    background: '#dbeafe',
                    padding: '2px 10px',
                    borderRadius: 12
                  }}>
                    {images.length}개
                  </span>
                </h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  {images.length > 1 && (
                    <button
                      onClick={handleDownloadAll}
                      disabled={isDownloading}
                      className="result-view-modal__download-button"
                      style={{
                        background: isDownloading ? '#9ca3af' : '#0fb37a',
                        cursor: isDownloading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isDownloading ? '⏳ 다운로드 중...' : '📦 ZIP 다운로드'}
                    </button>
                  )}
                  <button
                    onClick={() => handleDownloadImage(images[currentImageIndex])}
                    disabled={isDownloading}
                    className="result-view-modal__download-button"
                    style={{ cursor: isDownloading ? 'not-allowed' : 'pointer' }}
                  >
                    💾 현재 이미지 다운로드
                  </button>
                </div>
              </div>

              {/* 이미지 뷰어 */}
              <div className="result-view-modal__image-viewer">
                {images.length > 1 && (
                  <button onClick={handlePrevImage} className="result-view-modal__nav-button" aria-label="이전 이미지">
                    ←
                  </button>
                )}
                
                <div className="result-view-modal__image-container">
                  <img 
                    src={images[currentImageIndex]} 
                    alt={`결과물 이미지 ${currentImageIndex + 1}`}
                    className="result-view-modal__image"
                    onClick={() => setZoomedImage(images[currentImageIndex])}
                  />
                  {images.length > 1 && (
                    <div className="result-view-modal__image-counter">
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  )}
                </div>
                
                {images.length > 1 && (
                  <button onClick={handleNextImage} className="result-view-modal__nav-button" aria-label="다음 이미지">
                    →
                  </button>
                )}
              </div>

              {/* 썸네일 */}
              {images.length > 1 && (
                <div className="result-view-modal__thumbnails">
                  {images.map((img, idx) => (
                    <div
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className="result-view-modal__thumbnail"
                      style={{
                        border: idx === currentImageIndex 
                          ? '3px solid #2f6bff' 
                          : '2px solid #e2e8f0',
                        opacity: idx === currentImageIndex ? 1 : 0.6
                      }}
                    >
                      <img src={img} alt={`썸네일 ${idx + 1}`} className="result-view-modal__thumbnail-img" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* URL 섹션 */}
          {urls.length > 0 && (
            <div className="result-view-modal__section">
              <h3 className="result-view-modal__section-title">
                🔗 제출된 URL
                <span style={{ 
                  marginLeft: 8, 
                  fontSize: 13, 
                  fontWeight: 600, 
                  color: '#2f6bff',
                  background: '#dbeafe',
                  padding: '2px 10px',
                  borderRadius: 12
                }}>
                  {urls.length}개
                </span>
              </h3>
              <div className="result-view-modal__url-list">
                {urls.map((url, idx) => (
                  <div key={idx} className="result-view-modal__url-item">
                    <div className="result-view-modal__url-index">{idx + 1}</div>
                    <a 
                      href={normalizeUrl(url)} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="result-view-modal__url-link"
                    >
                      {url}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 메모 섹션 */}
          {note && (
            <div className="result-view-modal__section">
              <h3 className="result-view-modal__section-title">💬 인플루언서 메모</h3>
              <div className="result-view-modal__note-box">
                {note}
              </div>
            </div>
          )}

          {/* 결과물 없음 */}
          {images.length === 0 && urls.length === 0 && !note && (
            <div className="result-view-modal__empty-state">
              📭 제출된 결과물이 없습니다.
            </div>
          )}
        </div>

        {/* 닫기 버튼 */}
        <div className="mbm-foot">
          <button onClick={onClose} className="result-view-modal__confirm-button mb-btn mb-btn-primary">
            ✓ 확인
          </button>
        </div>
      </div>

      {/* 이미지 확대 모달 */}
      {zoomedImage && (
        <div 
          className="result-view-modal__zoom-overlay"
          onClick={(e) => {
            e.stopPropagation();
            setZoomedImage(null);
          }}
        >
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setZoomedImage(null);
            }}
            className="result-view-modal__zoom-close-button"
            aria-label="닫기"
          >
            ×
          </button>
          <img 
            src={zoomedImage} 
            alt="확대된 이미지" 
            className="result-view-modal__zoomed-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
